import {useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {pToast} from './PToast';
import ICON_COPY_LINK from '../../../public/static/share/icon-sns-copy.svg';
import ICON_FACEBOOK from '../../../public/static/share/icon-sns-fb.svg';
import ICON_KAKAO from '../../../public/static/share/icon-sns-kt.svg';
import ICON_LINE from '../../../public/static/share/icon-sns-line.svg';
import ICON_TWITTER from '../../../public/static/share/icon-sns-tw.svg';
import {getKakaoSdk} from '../../commonSub/function';
import Anchor from '../component/Anchor';
import {DESCRIPTION_MAP} from '../component/Meta';
import {pAlert} from '../component/PAlert';
import {REGION, SNS_IMAGE} from '../constant';
import {Page} from '../constant/page';
import {PageAppContext} from '../context';
import {openWindow, copyToClipboard} from '../function';
import useMounted from '../hook/useMounted';
import useTypedSelector from '../hook/useTypedSelector';
import {clientT} from '../i18n';
import {IRegion} from '../type';
import {ButtonSet} from '../type/alert';
import {getQueryStr, getCurrentPathOnClient} from '../util/PageRouter';
import {filteredJoin} from '../util/string';

interface IProps {
  url?: string;
  text?: string;
}

function ShareAlert({text, url = location.href}: IProps) {
  const {t} = useTranslation();
  const mounted = useMounted();

  const {page, title} = useContext(PageAppContext);
  const [content, episode, viewerContent] = useTypedSelector(state => [
    state.content.currentId
      ? state.content.contentMap[state.content.currentId]
      : undefined,
    state.viewer.episode,
    state.viewer.content,
  ]);

  function onClickCopyUrl() {
    copyToClipboard({
      copyText: url ?? getCurrentPathOnClient(),
      onSuccess: () => {
        pToast(t('share_link_copied_paste_toast'));
      },
    });
  }

  function shareKakaoTalk() {
    let description: string | undefined = DESCRIPTION_MAP[REGION];
    let imageUrl: string | undefined = SNS_IMAGE;

    if (page === Page.Content) {
      description = content?.synopsis;
      imageUrl = content?.shareImg;
    } else if (page === Page.Viewer) {
      description = viewerContent?.synopsis;
      imageUrl = episode?.thumbnail;
    }

    getKakaoSdk().Share.sendDefault({
      objectType: 'feed',
      content: {
        title,
        description,
        imageUrl,
        link: {
          mobileWebUrl: url,
          webUrl: url,
        },
        ...(page === Page.Viewer && {imageHeight: 260}),
      },
      buttons: [
        {
          title: '감상하기',
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
      ],
    });
  }

  if (!mounted) {
    return null;
  }

  return (
    <div className="flex justify-around mt-14">
      {SHAREABLE_REGION_LIST[REGION].map(({key, image}) => {
        return (
          <Anchor key={key}>
            <img
              className="sm:w-55 sm:h-55"
              onClick={() => {
                switch (key) {
                  case ShareType.CopyLink:
                    onClickCopyUrl();
                    break;
                  case ShareType.Kakao:
                    shareKakaoTalk();
                    break;
                  default:
                    showShareWindow(key, {
                      text,
                      url,
                    });
                    break;
                }
              }}
              width={44}
              height={44}
              src={image}
              alt={key}
            />
          </Anchor>
        );
      })}
    </div>
  );
}

enum ShareType {
  Line = 'Line',
  Kakao = 'Kakao',
  Facebook = 'Facebook',
  Twitter = 'Twitter',
  Whatsapp = 'Whatsapp',
  CopyLink = 'CopyLink',
}

interface IShareIcon {
  key: ShareType;
  text: string;
  image: string;
}

const SHARE_KAKAO: IShareIcon = {
  key: ShareType.Kakao,
  text: 'common_kakao',
  image: ICON_KAKAO,
};

const SHARE_LINE: IShareIcon = {
  key: ShareType.Line,
  text: 'common_line',
  image: ICON_LINE,
};

const SHARE_FACEBOOK: IShareIcon = {
  key: ShareType.Facebook,
  text: 'common_facebook',
  image: ICON_FACEBOOK,
};

const SHARE_COPY: IShareIcon = {
  key: ShareType.CopyLink,
  text: 'common_copy',
  image: ICON_COPY_LINK,
};

const SHARE_TWITTER: IShareIcon = {
  key: ShareType.Twitter,
  text: 'common_twitter',
  image: ICON_TWITTER,
};

const SHAREABLE_REGION_LIST: {
  [key in IRegion]: IShareIcon[];
} = {
  kor: [SHARE_KAKAO, SHARE_FACEBOOK, SHARE_TWITTER, SHARE_COPY],
  twn: [SHARE_FACEBOOK, SHARE_LINE, SHARE_TWITTER, SHARE_COPY],
  tha: [SHARE_FACEBOOK, SHARE_LINE, SHARE_TWITTER, SHARE_COPY],
  idn: [SHARE_FACEBOOK, SHARE_LINE, SHARE_TWITTER, SHARE_COPY],
};

const MAP_SHARE_URL = {
  [ShareType.Line]: 'https://social-plugins.line.me/lineit/share?',
  [ShareType.Facebook]: 'https://www.facebook.com/sharer/sharer.php?',
  [ShareType.Twitter]: 'https://twitter.com/share?',
  [ShareType.Whatsapp]: 'https://api.whatsapp.com/send?',
};

export function showShareAlert(
  data: {
    url?: string;
    text?: string;
  },
  showCustomOnly = false
) {
  if (!showCustomOnly && navigator.share) {
    if (!data.url) {
      data.url = location.href;
    }

    navigator
      .share({...data, title: clientT('common_share')})
      /**
       * AbortError: Abort due to cancellation of share.
       * NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.
       * safari에서 .share를 사용할 때 catch를 해주지 않으면 공유하다가 AbortError, NotAllowedError가 날 수 있다.
       */
      .catch(() => {});
  } else {
    pAlert({
      title: t => t('common_share'),
      desc: <ShareAlert {...data} />,
      buttonSet: ButtonSet.Cancel,
      labelClose: t => t('common_close'),
    });
  }
}

function showShareWindow(type: ShareType, data: {url: string; text?: string}) {
  openWindow({
    url: getShareUrl(type, data),
    windowName: '',
    features: 'height=400, width=550',
  });
}

function getShareUrl(type: ShareType, data: {url: string; text?: string}) {
  // TODO: 카카오톡 공유하기는 sdk 이용?
  // https://developers.kakao.com/tool/demo/link/feed
  if (type === ShareType.CopyLink || type === ShareType.Kakao) {
    return '';
  }
  const queryObj = getQueryObj(type, data);
  const queryString = getQueryStr(queryObj);
  const url = MAP_SHARE_URL[type];
  return url + queryString;
}

function getQueryObj(type: ShareType, data: {url: string; text?: string}) {
  const encodedUri = encodeURIComponent(data.url);
  switch (type) {
    case ShareType.Facebook:
      return {u: encodedUri};
    case ShareType.Twitter:
      return {
        url: encodedUri,
        text: data.text,
      };
    case ShareType.Whatsapp:
      return {
        text: filteredJoin([data.text, encodedUri], ' '),
      };
    case ShareType.Line:
      return {url: encodedUri};
    // TODO: 카카오톡 공유하기 관련 로직 추가해야된다.
    case ShareType.Kakao:
      return {url: encodedUri};
    default:
      return {};
  }
}
